import { Box, Button, Chip, Grid, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { Save } from '@mui/icons-material';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import {
  addOutOfStockItem,
  allOutOfStockIngredientsCacheKey,
  removeOutOfStock,
  updateOutOfStockItem,
  useAllIngredients,
  useAllOutOfStockIngredients,
  useIngredient,
} from '../../apis/products-api';
import { IngredientDto, OutOfStockIngredientDto } from '@kotipizzagroup/kotipizza-products-api-client';
import { isScheduleInEffect } from '../../shared/utils/outOfStockFilters';
import LoadingOverlay from '../../shared/LoadingOverlay/LoadingOverlay';
import { SearchInput } from '../../shared/Search/SearchInput';
import { GridColDef } from '@mui/x-data-grid';
import DataGrid from '../../shared/DataGrid/StyledDataGrid';
import { useCustomSnackBar } from '../../shared/SnackBar/useCustomSnackBar';
import { useQueryClient } from '@tanstack/react-query';
import { OutOfStockRow } from '.';
import { BottomBar, TopBar } from '../ProductsPage/ProductDetails';
import dayjs from 'dayjs';
import OutOfStockScheduleSelect from './OutOfStockScheduleSelect';
import { useAllRestaurants } from '../../apis/shops-api';

type OutOfStockIngredientFormData = {
  outOfStocks: OutOfStockIngredientDto[];
};

export const OutOfStockIngredientDetails = () => {
  const { id } = useParams<{ id?: string }>();
  const [ingredientId, setIngredientId] = useState(isNaN(Number(id)) ? 0 : Number(id));
  const [isSaving, setIsSaving] = useState(false);

  const { t } = useTranslation();
  const snackBar = useCustomSnackBar();
  const queryClient = useQueryClient();

  const isNew = ingredientId === 0;

  const createNewOutOfStockIngredient = (restaurantId: string): OutOfStockIngredientDto => ({
    ingredientId,
    externalShopId: restaurantId,
  });

  const { handleSubmit, control, reset } = useForm<OutOfStockIngredientFormData>({
    defaultValues: {
      outOfStocks: [],
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'outOfStocks',
  });

  const appendOutOfStockIngredient = (ingredient: OutOfStockIngredientDto) => {
    const restaurantFoundFromList = fields.find((f) => f.externalShopId === ingredient.externalShopId);
    if (restaurantFoundFromList) {
      if (dayjs(restaurantFoundFromList.scheduledUntil) <= dayjs()) {
        const index = fields.findIndex(
          (f) => f.externalShopId === ingredient.externalShopId && f.ingredientId === ingredient.ingredientId
        );
        update(index, ingredient);
        return;
      }
      snackBar.showError(t('Restaurant is added already'));
      return;
    }
    append(ingredient);
  };

  const updateOutOfStockIngredient = (ingredient: OutOfStockIngredientDto) => {
    const index = fields.findIndex(
      (f) => f.externalShopId === ingredient.externalShopId && f.ingredientId === ingredient.ingredientId
    );
    update(index, ingredient);
  };

  const ingredientColumns: GridColDef<IngredientDto>[] = [
    {
      field: 'name',
      headerName: t('Name'),
      flex: 1,
      renderCell: (params) => {
        return params.value;
      },
    },
    {
      field: 'isActive',
      headerName: t('Status'),
      flex: 1,
      renderCell: (params) => {
        return params.value ? (
          <Chip color="primary" label={t('shared.activeLabel.active')} />
        ) : (
          <Chip color="error" label={t('shared.activeLabel.inactive')} />
        );
      },
    },
  ];

  const handleScheduleChange = (date: string | null, ingredientId: number, shopExternalId: string) => {
    updateOutOfStockIngredient({
      ingredientId,
      externalShopId: shopExternalId,
      scheduledUntil: date,
    });
  };

  const restaurantColumns: GridColDef<OutOfStockRow>[] = [
    {
      field: 'externalShopId',
      headerName: 'Id',
      renderCell: (params) => {
        return params.value;
      },
    },
    {
      field: 'name',
      headerName: t('Name'),
      flex: 1,
      renderCell: (params) => {
        if (!params.row.restaurantName) return null;
        return params.row.restaurantName;
      },
    },
    {
      field: 'scheduledUntil',
      headerName: t('Scheduled until'),
      flex: 1,
      renderCell: (params) => {
        if (!params.row.item.scheduledUntil) return null;
        return dayjs(params.row.item.scheduledUntil).format('D.M.YYYY');
      },
    },
    {
      field: 'schedule',
      headerName: t('Schedule'),
      width: 200,
      renderCell: (params) => {
        return (
          <OutOfStockScheduleSelect
            value={params.row.item.scheduledUntil}
            allowIndefinite={!shopIdsLockedToSchedule.has(params.row.item.externalShopId)}
            onChange={(date) => handleScheduleChange(date, params.row.itemId, params.row.item.externalShopId)}
          />
        );
      },
    },
    {
      field: 'remove',
      headerName: t('Unlink'),
      width: 200,
      renderCell: (params) => {
        const index = fields.findIndex((f) => f.externalShopId === params.row.item.externalShopId);

        return (
          <Button color="error" variant="outlined" onClick={() => remove(index)}>
            {t('Unlink')}
          </Button>
        );
      },
    },
  ];

  const {
    data: ingredient,
    isInitialLoading: isInitialLoadingIngredient,
    isFetching: isFetchingIngredient,
  } = useIngredient(ingredientId, { enabled: !isNew });
  const { data: outOfStockIngredients, isInitialLoading: isOutOfStockIngredientsInitialLoading } =
    useAllOutOfStockIngredients();
  const { data: ingredients, isInitialLoading: isInitialLoadingIngredients } = useAllIngredients(false);
  const { data: restaurants, isInitialLoading: isInitialLoadingRestaurants } = useAllRestaurants();

  const outOfStocks = useMemo(() => {
    return outOfStockIngredients?.filter((i) => i.ingredientId === ingredientId) || [];
  }, [outOfStockIngredients, ingredientId]);

  /**
   * Restaurants whose stored entry already has a return date. The update endpoint cannot clear a
   * schedule back to open-ended, so these rows - including expired ones being marked out of stock
   * again - may only be given a new date, never "Indefinitely".
   */
  const shopIdsLockedToSchedule = useMemo(
    () => new Set(outOfStocks.filter((i) => i.scheduledUntil).map((i) => i.externalShopId)),
    [outOfStocks]
  );

  const restaurantRows = useMemo((): OutOfStockRow[] => {
    return fields.filter(isScheduleInEffect).map((f) => ({
      ...f,
      item: f,
      itemId: f.ingredientId,
      restaurantName: restaurants?.find((r) => r.shopExternalId === f.externalShopId)?.displayName,
    }));
  }, [fields, restaurants]);

  const isInitialLoadComplete =
    !isInitialLoadingIngredient &&
    !isInitialLoadingIngredients &&
    !isOutOfStockIngredientsInitialLoading &&
    !isInitialLoadingRestaurants;

  useEffect(() => {
    if (!isInitialLoadComplete) return;
    reset({ outOfStocks: outOfStockIngredients?.filter((i) => i.ingredientId == ingredientId) });
  }, [isInitialLoadComplete, outOfStockIngredients, ingredientId, reset]);

  const isSaveDisabled = isNew || isFetchingIngredient;

  const handleSave: SubmitHandler<OutOfStockIngredientFormData> = async (formData) => {
    // A stored entry with a return date can only be given a new date, so re-marking one out of stock
    // takes effect only once a date is picked. Saving it with an empty schedule would do nothing at
    // all, so say that instead of reporting a save that did not happen.
    const rowsMissingSchedule = formData.outOfStocks.filter(
      (item) => shopIdsLockedToSchedule.has(item.externalShopId) && !item.scheduledUntil
    );
    if (rowsMissingSchedule.length > 0) {
      const restaurantNames = rowsMissingSchedule
        .map(
          (item) =>
            restaurants?.find((r) => r.shopExternalId === item.externalShopId)?.displayName ?? item.externalShopId
        )
        .join(', ');
      snackBar.showError(t('Select return date for: {{restaurants}}', { restaurants: restaurantNames }));
      return;
    }

    try {
      setIsSaving(true);

      for (const item of formData.outOfStocks) {
        // Find and post new items
        const itemExists = outOfStocks?.find((p) => p.externalShopId === item.externalShopId);
        if (!itemExists && item.ingredientId && item.externalShopId) {
          await addOutOfStockItem({
            ingredientId: item.ingredientId,
            externalShopId: item.externalShopId,
            scheduledUntil: item.scheduledUntil,
          });
        }
        // Update items when the schedule changed to a new date. Clearing a schedule back to
        // open-ended is not supported here: the update endpoint requires a non-null scheduledUntil.
        if (
          itemExists &&
          item.ingredientId &&
          item.externalShopId &&
          item.scheduledUntil &&
          item.scheduledUntil !== itemExists.scheduledUntil
        ) {
          await updateOutOfStockItem({
            ingredientId: item.ingredientId,
            externalShopId: item.externalShopId,
            scheduledUntil: item.scheduledUntil,
          });
        }
      }

      // Remove items
      for (const item of outOfStocks) {
        const shouldKeep = formData.outOfStocks.find((i) => i.externalShopId === item.externalShopId);
        if (!shouldKeep && item.ingredientId && item.externalShopId) {
          await removeOutOfStock({ ingredientId: item.ingredientId, externalShopId: item.externalShopId });
        }
      }

      setIsSaving(false);
      await queryClient.invalidateQueries([allOutOfStockIngredientsCacheKey]);
      snackBar.showSuccess(t('global.saveSucceeded'));
    } catch {
      snackBar.showError(t('global.saveFailed'));
      setIsSaving(false);
    }
  };

  const saveButton = (
    <LoadingButton
      onClick={handleSubmit(handleSave)}
      startIcon={<Save />}
      loading={isSaving}
      disabled={isSaveDisabled}
      variant="contained"
      color="primary"
    >
      {t('global.save')}
    </LoadingButton>
  );

  const handleAddRestaurant = (value: string) => {
    appendOutOfStockIngredient(createNewOutOfStockIngredient(value));
  };

  const handleSelectIngredient = (value: string) => {
    setIngredientId(Number(value));
  };

  const ingredientList: IngredientDto[] = useMemo(() => {
    if (!ingredient) return [];
    return [{ ...ingredient }];
  }, [ingredient]);

  return (
    <Box>
      {!isInitialLoadComplete && <LoadingOverlay />}
      <TopBar>
        <Typography variant="h1">
          {t('Out of stock products')} / {t('Mark as out of stock')}
        </Typography>
        {saveButton}
      </TopBar>
      <Grid container item justifyContent="space-between" alignItems="center" my={2}>
        <Grid item xs={12} md={4}>
          <Typography variant="h2">{t('Out of stock ingredient')}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          {isNew && (
            <SearchInput
              values={ingredients?.map((p) => ({ value: String(p.ingredientId), label: p.name })) || []}
              noLabelPrefix={true}
              onSelect={handleSelectIngredient}
              label={t('Select ingredient')}
            />
          )}
        </Grid>
      </Grid>
      {ingredient && (
        <DataGrid
          disableRowSelectionOnClick
          rows={ingredientList}
          columns={ingredientColumns}
          getRowId={(row) => row.ingredientId || ''}
          loading={false}
          hideFooter={true}
        />
      )}

      {!isNew && (
        <div>
          <Grid container item justifyContent="space-between" alignItems="center" my={2} marginTop={6}>
            <Grid item xs={12} md={4}>
              <Typography variant="h2">{t('Out of stock from restaurant')}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <SearchInput
                values={restaurants?.map((r) => ({ value: r.shopExternalId, label: r.displayName })) || []}
                onSelect={handleAddRestaurant}
                noLabelPrefix={true}
                label={t('Search restaurant')}
              />
            </Grid>
          </Grid>
          <DataGrid
            disableRowSelectionOnClick
            rows={restaurantRows}
            columns={restaurantColumns}
            getRowId={(row) => row.item.externalShopId}
            loading={false}
          />
        </div>
      )}

      <Grid item xs={12} container marginTop={8}>
        {saveButton}
      </Grid>
      <BottomBar />
    </Box>
  );
};
